import { View, StyleSheet, TouchableOpacity, Animated } from "react-native";
import { useEffect, useRef, useState } from "react";
import { useDevice } from "../../_layout";
import Colors from "../../../Resources/Color";

/**
 * Individual step indicator with animation
 */
const StepDot = ({
  isActive,
  index,
  onPress,
  disabled,
  wasActive,
  isClickable,
}) => {
  const width = useRef(new Animated.Value(isActive ? 20 : 6)).current;
  const backgroundColor = useRef(new Animated.Value(isActive ? 1 : 0)).current;
  const scale = useRef(new Animated.Value(1)).current;
  const [isHovered, setIsHovered] = useState(false);
  const { isMobile } = useDevice();

  useEffect(() => {
    if (isActive && wasActive === false) {
      // Becoming active: First change color to black, then expand width
      Animated.sequence([
        Animated.timing(backgroundColor, {
          toValue: 1,
          duration: 150,
          useNativeDriver: false,
        }),
        Animated.timing(width, {
          toValue: 20,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    } else if (!isActive && wasActive === true) {
      // Becoming inactive: Shrink width (delayed to match when new dot starts expanding)
      Animated.sequence([
        Animated.delay(150), // Wait for the new dot's color change
        Animated.parallel([
          Animated.timing(width, {
            toValue: 6,
            duration: 250, // Same duration as expansion
            useNativeDriver: false,
          }),
          Animated.timing(backgroundColor, {
            toValue: 0,
            duration: 250,
            useNativeDriver: false,
          }),
        ]),
      ]).start();
    }
  }, [isActive]);

  useEffect(() => {
    Animated.spring(scale, {
      toValue: isHovered && !isActive && isClickable ? 1.6 : 1,
      useNativeDriver: true,
      tension: 300,
      friction: 10,
    }).start();
  }, [isHovered, isActive, isClickable]);

  const interpolatedColor = backgroundColor.interpolate({
    inputRange: [0, 1],
    outputRange: [Colors.grayColor, Colors.blackColor],
  });

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled || !isClickable}
      onMouseEnter={() =>
        !isMobile && !disabled && !isActive && isClickable && setIsHovered(true)
      }
      onMouseLeave={() => setIsHovered(false)}
    >
      <Animated.View
        style={[
          styles.indicator,
          {
            width: width,
            backgroundColor: interpolatedColor,
            opacity: isActive ? 1 : 0.3,
            transform: [{ scale: scale }],
          },
        ]}
      />
    </TouchableOpacity>
  );
};

/**
 * OnboardingStepIndicator component
 * Displays progress through onboarding steps with dots and a line for the active step
 * @param {number} currentStep - The current active step (0-indexed)
 * @param {number} totalSteps - Total number of onboarding steps
 * @param {function} onStepPress - Optional callback when a step is pressed (for testing)
 */
const OnboardingStepIndicator = ({ currentStep, totalSteps, onStepPress }) => {
  const previousStep = useRef(currentStep);

  useEffect(() => {
    previousStep.current = currentStep;
  }, [currentStep]);

  return (
    <View style={styles.container}>
      {Array.from({ length: totalSteps }).map((_, index) => (
        <StepDot
          key={index}
          isActive={index === currentStep}
          wasActive={index === previousStep.current}
          index={index}
          onPress={() => onStepPress && onStepPress(index)}
          disabled={!onStepPress}
          isClickable={index < currentStep}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  indicator: {
    height: 6,
    borderRadius: 3,
  },
});

export default OnboardingStepIndicator;
